import { DEFAULT_VIEWER, type PersonId, type ViewerId } from "../data/model";
import type { Repository } from "../data/repository";
import type { LensProvider } from "../lens/lens";
import type { LLMProvider } from "../llm/provider";
import { el, clear } from "../ui/dom";
import { currentTheme, toggleTheme } from "../ui/theme";
import { renderPeople } from "../ui/views/people";
import { renderPerson } from "../ui/views/person";
import { renderRankings } from "../ui/views/rankings";
import { renderSurface } from "../ui/views/surface";
import { renderAssemble } from "../ui/views/assemble";
import { renderSettings } from "../ui/views/settings";
import { renderPrivacy } from "../ui/views/privacy";
import { storageBannerEl, storageBannerVisible } from "../ui/storage";
import { openFeedbackPanel, openSharePanel } from "../ui/feedback";
import { openAboutPanel } from "../ui/about";
import { renderBrand } from "../ui/brand";

export type Route =
  | { name: "people" }
  | { name: "person"; id: PersonId }
  | { name: "rankings" }
  | { name: "surface" }
  | { name: "assemble"; prompt?: string }
  | { name: "settings" }
  | { name: "privacy" };

export interface AppContext {
  repo: Repository;
  lens: LensProvider;
  provider: LLMProvider;
  viewer: ViewerId;
  navigate(route: Route): void;
  refresh(): void;
}

// The always-visible footer disclaimer. The legal copy is verbatim; the final
// "Privacy notice" is a working link to the privacy page (built in renderChrome).
const DISCLAIMER =
  "Trust Lens is a private tool for recording your own professional judgements " +
  "about people. Your data is stored only in this browser, on this device, not " +
  "on STRAD's servers. Using the AI features sends the notes you have written, " +
  "which may name other people, to our AI provider to produce the analysis. " +
  "Trust Lens is not investment advice and does not rate funds or regulated " +
  "products. See the ";

export class App {
  // The app is built around the decision job, so Assemble is the front door.
  private route: Route = { name: "assemble" };
  private viewHost!: HTMLElement;
  private bannerHost!: HTMLElement;
  private nav!: HTMLElement;
  private readonly ctx: AppContext;

  constructor(
    private readonly root: HTMLElement,
    repo: Repository,
    lens: LensProvider,
    provider: LLMProvider,
  ) {
    this.ctx = {
      repo,
      lens,
      provider,
      viewer: DEFAULT_VIEWER,
      navigate: (route) => {
        this.route = route;
        void this.renderView();
      },
      refresh: () => void this.renderView(),
    };
  }

  start(): void {
    this.renderChrome();
    // Assemble is the front door and handles the empty state itself (the purpose
    // chooser), so there is no separate onboarding gate.
    void this.renderView();
  }

  private renderChrome(): void {
    clear(this.root);
    this.viewHost = el("div", { class: "view" });
    this.bannerHost = el("div", { class: "banner-host" });
    this.nav = el("nav", { class: "nav" });

    const themeToggle = el("button", {
      class: "toggle",
      type: "button",
      text: currentTheme() === "dark" ? "Light" : "Dark",
      on: {
        click: (e) => {
          (e.currentTarget as HTMLButtonElement).textContent =
            toggleTheme() === "dark" ? "Light" : "Dark";
        },
      },
    });

    const settingsBtn = el("button", {
      class: "toggle",
      type: "button",
      text: "Settings",
      on: { click: () => this.ctx.navigate({ name: "settings" }) },
    });

    this.root.append(
      el(
        "header",
        { class: "masthead" },
        el(
          "div",
          { class: "masthead__inner" },
          renderBrand(() => this.ctx.navigate({ name: "assemble" })),
          this.nav,
          el("span", { class: "masthead__spacer" }),
          settingsBtn,
          themeToggle,
        ),
      ),
      el(
        "main",
        { class: "main" },
        el("div", { class: "main__inner" }, this.bannerHost, this.viewHost),
      ),
      el(
        "footer",
        { class: "footer" },
        el(
          "div",
          { class: "footer__inner" },
          el(
            "p",
            { class: "disclaimer" },
            DISCLAIMER,
            el("button", {
              class: "link-inline",
              type: "button",
              text: "Privacy notice",
              on: { click: () => this.ctx.navigate({ name: "privacy" }) },
            }),
            ".",
          ),
          el(
            "div",
            { class: "footer__links" },
            el("button", {
              class: "link-inline",
              type: "button",
              text: "Feedback",
              on: { click: () => openFeedbackPanel(this.route.name) },
            }),
            el("button", {
              class: "link-inline",
              type: "button",
              text: "Share Trust Lens",
              on: { click: () => openSharePanel() },
            }),
            el("button", {
              class: "link-inline",
              type: "button",
              text: "About STRAD",
              on: { click: () => openAboutPanel() },
            }),
          ),
        ),
      ),
    );
  }

  private syncNav(): void {
    clear(this.nav);
    const r = this.route.name;
    const link = (label: string, active: boolean, route: Route) =>
      el("button", {
        class: `nav__link${active ? " is-active" : ""}`,
        text: label,
        on: { click: () => this.ctx.navigate(route) },
      });
    this.nav.append(
      link("Assemble", r === "assemble", { name: "assemble" }),
      link("People", r === "people" || r === "person", { name: "people" }),
      link("Surface", r === "surface", { name: "surface" }),
      link("Rankings", r === "rankings", { name: "rankings" }),
    );
  }

  private async renderView(): Promise<void> {
    this.syncNav();
    void this.renderStorageBanner();
    try {
      const view = await this.viewFor();
      clear(this.viewHost);
      this.viewHost.append(view);
    } catch (e) {
      clear(this.viewHost);
      this.viewHost.append(
        el(
          "div",
          { class: "notice" },
          el("p", { text: "Something went wrong loading this view. Try again." }),
        ),
      );
      console.error(e);
    }
  }

  // The honest not-persistent storage banner, mounted app-wide above the view.
  private async renderStorageBanner(): Promise<void> {
    if (!this.bannerHost) return;
    const show = await storageBannerVisible(this.ctx);
    clear(this.bannerHost);
    if (show) {
      this.bannerHost.append(
        storageBannerEl(this.ctx, () => void this.renderStorageBanner()),
      );
    }
  }

  private viewFor(): Promise<HTMLElement> {
    switch (this.route.name) {
      case "people":
        return renderPeople(this.ctx);
      case "person":
        return renderPerson(this.ctx, this.route.id);
      case "rankings":
        return renderRankings(this.ctx);
      case "surface":
        return renderSurface(this.ctx);
      case "assemble":
        return renderAssemble(this.ctx, this.route.prompt);
      case "settings":
        return renderSettings(this.ctx);
      case "privacy":
        return renderPrivacy(this.ctx);
    }
  }
}
